import React from 'react'
import { Box, Container, Typography, Stack, Paper } from '@mui/material'

export interface EmotionalNarrativeSectionProps {
  /**
   * Section heading
   */
  title: string

  /**
   * Array of paragraph content
   */
  paragraphs: string[]

  /**
   * Optional mission statement or tagline
   */
  mission?: string
}

/**
 * Emotional narrative section component
 * Presents compelling content about children and parenting to engage donors emotionally
 */
export const EmotionalNarrativeSection: React.FC<
  EmotionalNarrativeSectionProps
> = ({ title, paragraphs, mission }) => {
  return (
    <Box
      component="section"
      sx={{
        py: { xs: 6, sm: 8, md: 10 },
        bgcolor: 'grey.50',
      }}
      aria-labelledby="narrative-title"
    >
      <Container maxWidth="md" sx={{ px: { md: 15 } }}>
        <Stack spacing={{ xs: 3, sm: 4 }}>
          <Typography
            id="narrative-title"
            variant="h2"
            component="h2"
            sx={{
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
              fontWeight: 700,
              color: 'text.primary',
              textAlign: 'center',
              mb: '0.25em !important',
            }}
          >
            {title}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontSize: { xs: '1.25rem', sm: '1.75rem' },
              color: 'text.secondary',
              textAlign: 'center',
              fontStyle: 'italic',
              mt: '0 !important',
            }}
          >
            A letter from the founder
          </Typography>
          <Paper elevation={2}>
            <Box p={4}>
              <Typography
                variant="body1"
                sx={{
                  fontSize: { xs: '1rem', sm: '1.0625rem', md: '1.125rem' },
                  lineHeight: 1.8,
                  color: 'text.secondary',
                  maxWidth: '800px',
                  mx: 'auto',
                  mb: 2,
                }}
              >
                Dear Community,
              </Typography>
              {paragraphs.map((paragraph, index) => (
                <Typography
                  key={index}
                  variant="body1"
                  sx={{
                    fontSize: { xs: '1rem', sm: '1.0625rem', md: '1.125rem' },
                    lineHeight: 1.8,
                    color: 'text.secondary',
                    maxWidth: '800px',
                    mx: 'auto',
                    mb: index === 0 || index === 1 ? 2 : 0,
                  }}
                >
                  {paragraph}
                </Typography>
              ))}
              {mission && (
                <Typography
                  variant="h6"
                  component="p"
                  sx={{
                    fontWeight: 600,
                    color: 'primary.main',
                    textAlign: 'left',
                    mt: { xs: 2, sm: 3 },
                  }}
                >
                  {mission}
                </Typography>
              )}
              <Typography
                variant="body1"
                sx={{
                  fontSize: { xs: '1rem', sm: '1.0625rem', md: '1.125rem' },
                  lineHeight: 1.8,
                  color: 'text.secondary',
                  maxWidth: '800px',
                  mx: 'auto',
                  mt: 3,
                  textAlign: 'left',
                }}
              >
                Sincerely,
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontFamily:
                    "'Brush Script MT', 'Lucida Handwriting', cursive",
                  fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                  color: 'primary.main',
                  maxWidth: '800px',
                  mx: 'auto',
                  mt: 0,
                  mb: 0,
                  textAlign: 'left',
                  fontStyle: 'italic',
                }}
              >
                Mark Ludwig
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '1rem' },
                  color: 'text.secondary',
                  maxWidth: '800px',
                  mx: 'auto',
                  textAlign: 'left',
                }}
              >
                Founder & President
              </Typography>
            </Box>
          </Paper>
        </Stack>
      </Container>
    </Box>
  )
}
